import { QueryKey } from '@tanstack/query-core';
import { UseQueryOptions } from '@tanstack/react-query';

export enum EndpointType {
    /** protobuf based endpoint */
    Rpc = 'rpc',
    /** simple JSON POST endpoint */
    Post = 'post',
    /** simple JSON GET endpoint */
    Get = 'get',
}

/**
 * usable content types for protobuf based endpoints
 */
export enum ContentType {
    /** binary format */
    Protobuf = 'application/x-protobuf',
    /** JSON format */
    Json = 'application/json',
}

/**
 * extended Response type to hold the parsed body and the signal if present
 */
export interface HttpResponse<Type> extends Response {
    parsedBody?: Type;
    signal?: AbortSignal;
}

/**
 * the call options for the given call
 */
export interface CallOptions<RequestType, UrlParams> {
    url: string | URL;
    requestOptions?: RequestInit;
    queryParams?: URLSearchParams;
    requestData?: RequestType;
    urlParams?: RequestType | UrlParams;
}

/**
 * type of the request builder function
 */
export type RequestBuilder<RequestType, UrlParams> = (
    options: CallOptions<RequestType, UrlParams>
) => Request;

/**
 * type of the response parser function
 */
export type ResponseParser<ResponseType> = (
    response: Response,
    signal?: AbortSignal
) => Promise<ResponseType>;

export class FetchError extends Error {}

/**
 * base interface for protobuf message encoder
 */
export interface RequestDataEncoder<Type> {
    encode(data: Type): {
        finish(): Uint8Array;
    };

    fromJSON(data: Type): Type;
}

/**
 * base interface for protobuf message decoder
 */
export interface ResponseDecoder<Type> {
    decode(input: Uint8Array): Type;

    fromJSON(data: unknown): Type;
}

/**
 * base type of usable type maps
 */
export type TypeMapType = {
    [Endpoint in string | number | symbol]: EndpointTypeDefinition<
        unknown,
        unknown
    >;
};

/**
 * wrapper for ReactQuery options, to be optimized for endpoint config usage
 */
type QueryOptions<
    TypeMap extends TypeMapType,
    Endpoint extends keyof TypeMap,
    TError = unknown,
    TQueryKey extends QueryKey = QueryKey
> = Omit<
    UseQueryOptions<
        GetEndpointResponseType<TypeMap, Endpoint>,
        TError,
        GetEndpointResponseType<TypeMap, Endpoint>,
        TQueryKey
    >,
    | 'queryFn'
    | 'queryKey'
    | 'enabled'
    | 'retry'
    | 'retryDelay'
    | 'queryKeyHashFn'
    | 'refetchInterval'
    | 'refetchOnMount'
    | 'refetchOnWindowFocus'
    | 'refetchOnReconnect'
    | 'onSuccess'
    | 'onError'
    | 'onSettled'
    | 'select'
    | 'initialData'
    | 'initialDataUpdatedAt'
    | 'placeholderData'
    | 'useErrorBoundary'
> & {
    retry?: boolean | number;
    retryDelay?: number;
    refetchInterval?: number | false;
    refetchOnMount?: boolean | 'always';
    refetchOnWindowFocus?: boolean | 'always';
    refetchOnReconnect?: boolean | 'always';
    useErrorBoundary?: boolean;
};

/**
 * interface for rpc Request and Response converter instances
 */
export interface RpcConverter<
    TypeMap extends TypeMapType,
    Endpoint extends keyof TypeMap
> {
    Request: RequestDataEncoder<GetEndpointRequestType<TypeMap, Endpoint>>;
    Response: ResponseDecoder<GetEndpointResponseType<TypeMap, Endpoint>>;
}

export type BaseUrlProvider = () => Promise<string>;

/**
 * basic endpoint definition for all end point types
 */
interface BaseEndpointDefinition<
    TypeMap extends TypeMapType,
    Endpoint extends keyof TypeMap,
    TError = unknown,
    TQueryKey extends QueryKey = QueryKey
> {
    url?: string;
    baseUrlProvider?: BaseUrlProvider;
    timeout?: number;
    queryOptions?: QueryOptions<TypeMap, Endpoint, TError, TQueryKey>;
}

/**
 * endpoint definition for simple JSON endpoints
 */
interface LegacyEndpointDefinition<
    TypeMap extends TypeMapType,
    Endpoint extends keyof TypeMap,
    TError = unknown,
    TQueryKey extends QueryKey = QueryKey
> extends BaseEndpointDefinition<TypeMap, Endpoint, TError, TQueryKey> {
    type: EndpointType.Get | EndpointType.Post;
}

/**
 * interface for rpc converter provider definitions in config
 */
type ConverterProvider<
    TypeMap extends TypeMapType,
    Endpoint extends keyof TypeMap
> = () => Promise<RpcConverter<TypeMap, Endpoint>>;

/**
 * endpoint definition for protobuf based endpoints
 */
interface RpcEndpointDefinition<
    TypeMap extends TypeMapType,
    Endpoint extends keyof TypeMap,
    TError = unknown,
    TQueryKey extends QueryKey = QueryKey
> extends BaseEndpointDefinition<TypeMap, Endpoint, TError, TQueryKey> {
    type: EndpointType.Rpc;
    converterProvider: ConverterProvider<TypeMap, Endpoint>;
}

/**
 * combined type guard type for all endpoint definitions
 */
export type EndpointDefinition<
    TypeMap extends TypeMapType,
    Endpoint extends keyof TypeMap,
    TError = unknown,
    TQueryKey extends QueryKey = QueryKey
> =
    | RpcEndpointDefinition<TypeMap, Endpoint, TError, TQueryKey>
    | LegacyEndpointDefinition<TypeMap, Endpoint, TError, TQueryKey>;

/**
 * type for endpoint config
 */
export type EndpointConfig<TypeMap extends TypeMapType> = {
    [Endpoint in keyof TypeMap]: EndpointDefinition<TypeMap, Endpoint>;
};

/**
 * base interface for endpoint type definitions used in type map
 */
export interface EndpointTypeDefinition<
    Request,
    Response,
    UrlParams extends Record<string, unknown> = Partial<Request>
> {
    Request: Request;
    Response: Response;
    UrlParams: UrlParams;
}

/**
 * helper type for extracting the request type from endpoint type definition
 */
type GetEndpointRequestDefinition<
    TypeDefinition extends EndpointTypeDefinition<unknown, unknown>
> = TypeDefinition extends EndpointTypeDefinition<infer Request, unknown>
    ? Request
    : never;

/**
 * helper type for extracting the response type from endpoint type definition
 */
type GetEndpointResponseDefinition<
    TypeDefinition extends EndpointTypeDefinition<unknown, unknown>
> = TypeDefinition extends EndpointTypeDefinition<unknown, infer Response>
    ? Response
    : never;

/**
 * helper type for extracting the url params type from endpoint type definition
 */
type GetEndpointUrlParamsDefinition<
    TypeDefinition extends EndpointTypeDefinition<unknown, unknown>
> = TypeDefinition extends EndpointTypeDefinition<
    unknown,
    unknown,
    infer UrlParams
>
    ? UrlParams
    : never;

/**
 * helper type to get request type from endpoint
 */
export type GetEndpointRequestType<
    TypeMap extends TypeMapType,
    Endpoint extends keyof TypeMap
> = GetEndpointRequestDefinition<TypeMap[Endpoint]>;

/**
 * helper type to get response type from endpoint
 */
export type GetEndpointResponseType<
    TypeMap extends TypeMapType,
    Endpoint extends keyof TypeMap
> = GetEndpointResponseDefinition<TypeMap[Endpoint]>;

/**
 * helper type to get url params type from endpoint
 */
export type GetEndpointUrlParamsType<
    TypeMap extends TypeMapType,
    Endpoint extends keyof TypeMap
> = GetEndpointUrlParamsDefinition<TypeMap[Endpoint]>;

/**
 * final definition of options for callApiEndpoint
 */
export type ApiCallOptions<
    TypeMap extends TypeMapType,
    Endpoint extends keyof TypeMap
> = Partial<
    CallOptions<
        GetEndpointRequestType<TypeMap, Endpoint>,
        GetEndpointUrlParamsType<TypeMap, Endpoint>
    >
> & {
    abortController?: AbortController;
    timeout?: number;
};
